import { ActivityLogEntry, ActivityLogEntryTypeEnum } from "@mutiny-group/sbo-openapi-client";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";

export const getActivityLogV2 = (
  scenarioBuilderClient: ScenarioBuilderClient,
  scenarioId: string
) => {
  return scenarioBuilderClient.scenarios.getActivityLog({
    scenarioUuid: scenarioId,
  });
};

type UseActivityLogV2Options = {
  scenarioId: string;
  refetchInterval?: number;
  enabled?: boolean;
};

// Map new API status values to old API event types
const mapStatusToEventType = (
  status: string | null,
  entryType: ActivityLogEntryTypeEnum
): string => {
  // Handle comment type
  if (entryType === ActivityLogEntryTypeEnum.Comment) {
    return "comment";
  }

  // Handle status updates based on the status field
  if (entryType === ActivityLogEntryTypeEnum.StatusUpdate && status) {
    switch (status.toLowerCase()) {
      case "draft":
        return "created"; // Draft status means scenario was created
      case "submitted":
        return "run"; // Submitted means scenario was run/submitted
      case "ready_for_review":
        return "completed"; // Ready for review means scenario completed processing
      case "approved":
        return "approved"; // Approved status
      case "archived":
        return "archived"; // Archived status
      case "error":
        return "error"; // Error status
      default:
        // For any unknown status, treat as a generic status update
        return "completed";
    }
  }

  // Default fallback
  return "completed";
};

export const useActivityLogV2 = ({
  scenarioId,
  refetchInterval,
  enabled = false,
}: UseActivityLogV2Options): UseQueryResult<any, Error> => {
  const scenarioBuilderClient = useScenarioBuilderClient();

  return useQuery({
    queryKey: ["activity-log-v2", scenarioId, scenarioBuilderClient],
    queryFn: () => getActivityLogV2(scenarioBuilderClient, scenarioId),
    select: (data) => data.map((item) => transformActivityLogV2(item)),
    enabled,
    ...(refetchInterval !== undefined && { refetchInterval }),
  });
};

const transformActivityLogV2 = (entry: ActivityLogEntry) => {
  const eventType = mapStatusToEventType(entry.status ?? null, entry.type);

  return {
    event: {
      eventUuid: entry.commentId || `${entry.userId || "unknown"}-${entry.timestamp.getTime()}`,
      type: eventType,
      eventType: eventType, // Add eventType field that matches the original API
      payload: {
        comment: entry.comment,
        // Include additional payload data based on event type
        ...(eventType === "created" && { performedBy: entry.userId }),
        ...(eventType === "approved" && {
          performedBy: entry.userId,
          scenarioUuid: "",
        }),
        ...(eventType === "archived" && {
          performedBy: entry.userId,
          scenarioUuid: "",
        }),
      },
    },
    createdBy: {
      userId: entry.userId,
      name: entry.userName || entry.userId || "",
      email: entry.userEmail || "",
    },
    createdAt: entry.timestamp,
  };
};
